import React, { useState, useRef, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  OverlayTrigger,
  Popover,
} from "react-bootstrap";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { US_STATES } from "configurations/Constants";
import BackDropLoader from "components/common/BackDropLoader";
import RegistrationBanner from "./RegistrationBanner";
import ConfirmationCodePopup from "./ConfirmationCodePopup";
import "styles/Registration.css";
import { Link } from "react-router-dom";
import {
  USER_REGISTER_FAILED,
  PLEASE_ENTER_FIRST_NAME,
  PLEASE_ENTER_LAST_NAME,
  PLEASE_ENTER_TDA_NUMBER,
  EMAILS_DONT_MATCH,
  PLEASE_ENTER_MOBILE_NUMBER,
  PLEASE_ENTER_ADDRESS,
  PLEASE_ENTER_CITY,
  PLEASE_SELECT_STATE,
  PLEASE_ENTER_ZIP_CODE,
  PLEASE_ENTER_SIGNUP_CODE,
  PLEASE_ENTER_LEGAL_ENTITY,
  PLEASE_ENTER_VALID_EMAIL,
  PLEASE_ENTER_LEGAL_ENTITIES_NOTE,
  PLEASE_ENTER_PASSWORD,
  PLEASE_ENTER_CONFIRM_PASSWORD,
  PASSWORDS_DONT_MATCH,
  CONFIRMATION_CODE_SENT,
  PLEASE_ACCEPT_TERMS_AND_CONDITIONS,
  TDA_NUMBER_NOTE,
  LABEL_ENTER_FIRST_NAME,
  LABEL_ENTER_LAST_NAME,
  LABEL_ENTER_TDA_NUMBER,
  LABEL_ENTER_MOBILE_NUMBER,
  LABEL_ENTER_ADDRESS,
  LABEL_ENTER_CITY,
  LABEL_ENTER_ZIP_CODE,
  LABEL_ENTER_SIGNUP_CODE,
  LABEL_ENTER_LEGAL_ENTITY,
  LABEL_ENTER_PASSWORD,
  LABEL_ENTER_CONFIRM_PASSWORD,
  LABEL_ENTER_EMAIL,
  LABEL_SELECT_STATE,
  ADD_ENTITY,
} from "configurations/MessageConstants";
import UserService from "services/UserService";
import { toast } from "react-toastify";
import Lottie from "react-lottie";
import { USER_REGISTRATION_LOTTIE_OPTIONS } from "configurations/Constants";
import { AuthHelper } from "utils/AuthHelper";

const Registration = () => {
  const emailRef = useRef(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const defaultValues = {
    firstName: "",
    lastName: "",
    tdaNumber: "",
    email: "",
    confirmEmail: "",
    password: "",
    confirmPassword: "",
    mobileNumber: "",
    address: "",
    city: "",
    state: "Texas",
    zipCode: "",
    signUpCode: "",
    legalEntities: [],
    permissionConsent: false,
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
    watch,
    trigger,
  } = useForm({
    defaultValues,
    mode: "onChange",
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "legalEntities",
  });
  const email = watch("email");
  const confirmEmail = watch("confirmEmail");
  const password = watch("password");
  const confirmPassword = watch("confirmPassword");

  useEffect(() => {
    if (confirmPassword) {
      trigger("confirmPassword");
    }
  }, [password, trigger, confirmPassword]);

  useEffect(() => {
    if (confirmEmail) {
      trigger("confirmEmail");
    }
  }, [email, trigger, confirmEmail]);

  const handleMobileNumberChange = (field, e) => {
    const number = e.target.value.replace(/\D/g, "").slice(0, 10);
    field.onChange(number);
  };

  const onSubmit = async (data) => {
    try {
      const legalEntities = data.legalEntities.map((entity) => entity.value);
      await UserService.userRegister({
        ...data,
        legalEntities,
      });
      toast.success(CONFIRMATION_CODE_SENT);
      setShowConfirmationModal(true);
    } catch (error) {
      console.error(USER_REGISTER_FAILED, error);
      toast.error(
        <div>
          {USER_REGISTER_FAILED}
          <br />
          {error.response.data.detail}
        </div>
      );
    }
  };

  const handleCancel = () => {
    reset(defaultValues);
  };

  const handleConfirmationModalClose = () => {
    setShowConfirmationModal(false);
  };

  const addArrayItem = () => {
    append({ value: "" });
  };

  const removeArrayItem = (index) => {
    remove(index);
  };

  const handlePopover = (title, message) => {
    return (
      <Popover id="entity-info-popover">
        <Popover.Header as="h3">{title}</Popover.Header>
        <Popover.Body>{message}</Popover.Body>
      </Popover>
    );
  };

  return (
    <>
      {isSubmitting && (
        <BackDropLoader
          loader={
            <Lottie
              options={USER_REGISTRATION_LOTTIE_OPTIONS}
              height={200}
              width={200}
            />
          }
        />
      )}
      <RegistrationBanner />
      <ConfirmationCodePopup
        show={showConfirmationModal}
        onClose={handleConfirmationModalClose}
        email={watch("email")}
      />

      <Container className="mt-4 mb-5 registration-container">
        <div className="d-flex flex-column mb-3 align-items-end">
          <small>
            Already have an account?{" "}
            <Link to="/login" className="login-link-button">
              Login
            </Link>
          </small>
        </div>
        <Form onSubmit={handleSubmit(onSubmit)} noValidate autoComplete="off">
          <Row className="mb-3">
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>
                  First Name <span className="required-field">*</span>
                </Form.Label>
                <Controller
                  name="firstName"
                  control={control}
                  rules={{
                    required: PLEASE_ENTER_FIRST_NAME,
                    validate: (value) =>
                      value.trim() !== "" || PLEASE_ENTER_FIRST_NAME,
                  }}
                  render={({ field }) => (
                    <Form.Control
                      {...field}
                      type="text"
                      placeholder={LABEL_ENTER_FIRST_NAME}
                      className="input-underline"
                      isInvalid={!!errors.firstName}
                      maxLength={50}
                    />
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.firstName?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Last Name <span className="required-field">*</span>
                </Form.Label>
                <Controller
                  name="lastName"
                  control={control}
                  rules={{
                    required: PLEASE_ENTER_LAST_NAME,
                    validate: (value) =>
                      value.trim() !== "" || PLEASE_ENTER_LAST_NAME,
                  }}
                  render={({ field }) => (
                    <Form.Control
                      {...field}
                      type="text"
                      placeholder={LABEL_ENTER_LAST_NAME}
                      className="input-underline"
                      isInvalid={!!errors.lastName}
                      maxLength={50}
                    />
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.lastName?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>
                  TDA Number <span className="required-field">*</span>
                  <OverlayTrigger
                    trigger={["click"]}
                    placement="top"
                    overlay={handlePopover("TDA Number", TDA_NUMBER_NOTE)}
                    rootClose
                  >
                    <i className="fa fa-info-circle ms-2"></i>
                  </OverlayTrigger>
                </Form.Label>
                <Controller
                  name="tdaNumber"
                  control={control}
                  rules={{
                    required: PLEASE_ENTER_TDA_NUMBER,
                    validate: (value) =>
                      value.trim() !== "" || PLEASE_ENTER_TDA_NUMBER,
                  }}
                  render={({ field }) => (
                    <Form.Control
                      {...field}
                      type="text"
                      placeholder={LABEL_ENTER_TDA_NUMBER}
                      className="input-underline"
                      isInvalid={!!errors.tdaNumber}
                      maxLength={50}
                    />
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.tdaNumber?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Email <span className="required-field">*</span>
                </Form.Label>
                <Controller
                  name="email"
                  control={control}
                  rules={{
                    required: PLEASE_ENTER_VALID_EMAIL,
                    validate: (value) =>
                      AuthHelper.isValidEmail(value) ||
                      PLEASE_ENTER_VALID_EMAIL,
                  }}
                  render={({ field }) => (
                    <Form.Control
                      {...field}
                      type="email"
                      ref={emailRef}
                      placeholder={LABEL_ENTER_EMAIL}
                      className="input-underline"
                      autoComplete="email"
                      isInvalid={!!errors.email}
                      maxLength={50}
                    />
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.email?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Confirm Email <span className="required-field">*</span>
                </Form.Label>
                <Controller
                  name="confirmEmail"
                  control={control}
                  rules={{
                    required: PLEASE_ENTER_VALID_EMAIL,
                    validate: {
                      validEmail: (value) =>
                        AuthHelper.isValidEmail(value) ||
                        PLEASE_ENTER_VALID_EMAIL,
                      matchesEmail: (value) =>
                        value === email || EMAILS_DONT_MATCH,
                    },
                  }}
                  render={({ field }) => (
                    <Form.Control
                      {...field}
                      type="email"
                      placeholder={LABEL_ENTER_EMAIL}
                      className="input-underline"
                      autoComplete="email"
                      isInvalid={!!errors.confirmEmail}
                      maxLength={50}
                    />
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.confirmEmail?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Password <span className="required-field">*</span>
                </Form.Label>
                <div className="position-relative">
                  <Controller
                    name="password"
                    control={control}
                    rules={{
                      required: PLEASE_ENTER_PASSWORD,
                      validate: AuthHelper.isValidPassword,
                    }}
                    render={({ field }) => (
                      <Form.Control
                        {...field}
                        type={showPassword ? "text" : "password"}
                        placeholder={LABEL_ENTER_PASSWORD}
                        className="input-underline"
                        isInvalid={!!errors.password}
                        autoComplete="new-password"
                        maxLength={50}
                      />
                    )}
                  />
                  <Button
                    variant="link"
                    className="password-toggle-button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                  >
                    <i
                      className={`fa fa-eye${showPassword ? "" : "-slash"}`}
                    ></i>
                  </Button>
                  <Form.Control.Feedback type="invalid">
                    {errors.password?.message}
                  </Form.Control.Feedback>
                </div>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Confirm Password <span className="required-field">*</span>
                </Form.Label>
                <div className="position-relative">
                  <Controller
                    name="confirmPassword"
                    control={control}
                    rules={{
                      required: PLEASE_ENTER_CONFIRM_PASSWORD,
                      validate: {
                        notEmpty: (value) =>
                          value.trim() !== "" || PLEASE_ENTER_CONFIRM_PASSWORD,
                        matchesPassword: (value) =>
                          value === password || PASSWORDS_DONT_MATCH,
                      },
                    }}
                    render={({ field }) => (
                      <Form.Control
                        {...field}
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder={LABEL_ENTER_CONFIRM_PASSWORD}
                        className="input-underline"
                        isInvalid={!!errors.confirmPassword}
                        autoComplete="new-password"
                        maxLength={50}
                      />
                    )}
                  />
                  <Button
                    variant="link"
                    className="password-toggle-button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label={
                      showConfirmPassword
                        ? "Hide confirm password"
                        : "Show confirm password"
                    }
                  >
                    <i
                      className={`fa fa-eye${
                        showConfirmPassword ? "" : "-slash"
                      }`}
                    ></i>
                  </Button>
                  <Form.Control.Feedback type="invalid">
                    {errors.confirmPassword?.message}
                  </Form.Control.Feedback>
                </div>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Mobile Number <span className="required-field">*</span>
                </Form.Label>
                <Controller
                  name="mobileNumber"
                  control={control}
                  rules={{
                    required: PLEASE_ENTER_MOBILE_NUMBER,
                    validate: (value) =>
                      value.length === 10 || PLEASE_ENTER_MOBILE_NUMBER,
                  }}
                  render={({ field }) => (
                    <Form.Control
                      {...field}
                      type="text"
                      placeholder={LABEL_ENTER_MOBILE_NUMBER}
                      className="input-underline"
                      maxLength={10}
                      isInvalid={!!errors.mobileNumber}
                      onChange={(e) => handleMobileNumberChange(field, e)}
                    />
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.mobileNumber?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Address <span className="required-field">*</span>
                </Form.Label>
                <Controller
                  name="address"
                  control={control}
                  rules={{
                    required: PLEASE_ENTER_ADDRESS,
                    validate: (value) =>
                      value.trim() !== "" || PLEASE_ENTER_ADDRESS,
                  }}
                  render={({ field }) => (
                    <Form.Control
                      {...field}
                      type="text"
                      placeholder={LABEL_ENTER_ADDRESS}
                      className="input-underline"
                      isInvalid={!!errors.address}
                    />
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.address?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>
                  City <span className="required-field">*</span>
                </Form.Label>
                <Controller
                  name="city"
                  control={control}
                  rules={{
                    required: PLEASE_ENTER_CITY,
                    validate: (value) =>
                      value.trim() !== "" || PLEASE_ENTER_CITY,
                  }}
                  render={({ field }) => (
                    <Form.Control
                      {...field}
                      type="text"
                      placeholder={LABEL_ENTER_CITY}
                      className="input-underline"
                      isInvalid={!!errors.city}
                      maxLength={50}
                    />
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.city?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="state">
                  State <span className="required-field">*</span>
                </Form.Label>
                <Controller
                  name="state"
                  control={control}
                  rules={{ required: PLEASE_SELECT_STATE }}
                  render={({ field }) => (
                    <Form.Select
                      {...field}
                      id="state"
                      className="input-underline"
                      aria-label="Select State"
                      isInvalid={!!errors.state}
                    >
                      <option value="">{LABEL_SELECT_STATE}</option>
                      {US_STATES.map((stateValue) => (
                        <option key={stateValue} value={stateValue}>
                          {stateValue}
                        </option>
                      ))}
                    </Form.Select>
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.state?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Zip Code <span className="required-field">*</span>
                </Form.Label>
                <Controller
                  name="zipCode"
                  control={control}
                  rules={{
                    required: PLEASE_ENTER_ZIP_CODE,
                    validate: (value) =>
                      value.trim() !== "" || PLEASE_ENTER_ZIP_CODE,
                  }}
                  render={({ field }) => (
                    <Form.Control
                      {...field}
                      type="text"
                      placeholder={LABEL_ENTER_ZIP_CODE}
                      className="input-underline"
                      isInvalid={!!errors.zipCode}
                      maxLength={10}
                    />
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.zipCode?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Sign Up Code <span className="required-field">*</span>
                </Form.Label>
                <Controller
                  name="signUpCode"
                  control={control}
                  rules={{
                    required: PLEASE_ENTER_SIGNUP_CODE,
                    validate: (value) =>
                      value.trim() !== "" || PLEASE_ENTER_SIGNUP_CODE,
                  }}
                  render={({ field }) => (
                    <Form.Control
                      {...field}
                      type="text"
                      placeholder={LABEL_ENTER_SIGNUP_CODE}
                      className="input-underline"
                      isInvalid={!!errors.signUpCode}
                      maxLength={50}
                    />
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.signUpCode?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <div className="my-4">
            <div className="d-flex gap-3 align-items-center mb-2">
              <h5 className="mb-0 ms-1">
                Legal Entities
                <OverlayTrigger
                  trigger={["click"]}
                  placement="top"
                  overlay={handlePopover(
                    "Farm and Business Entities",
                    PLEASE_ENTER_LEGAL_ENTITIES_NOTE
                  )}
                  rootClose
                >
                  <i className="fa fa-info-circle ms-2"></i>
                </OverlayTrigger>
              </h5>
              <Button
                variant="outline-dark"
                className="d-flex"
                onClick={addArrayItem}
              >
                <small>{ADD_ENTITY}</small>
              </Button>
            </div>

            {fields.map((field, index) => (
              <Row key={field.id} className="mb-3 align-items-center">
                <Col>
                  <div className="d-flex align-items-center">
                    <Controller
                      name={`legalEntities.${index}.value`}
                      control={control}
                      rules={{
                        required: PLEASE_ENTER_LEGAL_ENTITY,
                        validate: (value) =>
                          value.trim() !== "" || PLEASE_ENTER_LEGAL_ENTITY,
                      }}
                      render={({ field: controllerField }) => (
                        <Form.Control
                          {...controllerField}
                          type="text"
                          placeholder={LABEL_ENTER_LEGAL_ENTITY}
                          className="input-underline"
                          isInvalid={!!errors.legalEntities?.[index]?.value}
                          maxLength={50}
                        />
                      )}
                    />
                    <Button
                      variant="link"
                      className="text-muted mx-3"
                      onClick={() => removeArrayItem(index)}
                    >
                      <i className="fa fa-trash"></i>
                    </Button>
                  </div>
                  <Form.Control.Feedback type="invalid">
                    {errors.legalEntities?.[index]?.message}
                  </Form.Control.Feedback>
                </Col>
              </Row>
            ))}
          </div>

          <div className="my-5">
            <h5 className="mb-4">Permissions and Consents</h5>
            <Controller
              name="permissionConsent"
              control={control}
              rules={{ required: PLEASE_ACCEPT_TERMS_AND_CONDITIONS }}
              render={({ field }) => (
                <Form.Check
                  type="checkbox"
                  aria-label="Permission Consent"
                  id="permission-consent"
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  label={
                    <>
                      I agree to the Chemical Application{" "}
                      <Link className="login-link-button">
                        Terms and Conditions
                      </Link>{" "}
                      &{" "}
                      <Link className="login-link-button">Privacy Policy</Link>
                    </>
                  }
                  isInvalid={!!errors.permissionConsent}
                />
              )}
            />
          </div>
          <div className="d-flex justify-content-end gap-2">
            <Button variant="outline-secondary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              Register
            </Button>
          </div>
        </Form>
      </Container>
    </>
  );
};

export default Registration;
